import { type ReactNode, useMemo, useState } from 'react';
import { InkBlock } from '@/components/atoms/InkBlock';
import { Checkbox } from '@/components/ui/checkbox';
import type { Spell } from '@/schemas/spell';
import { isAutoSpellGrantLevelRule } from '@/schemas/move';
import { isLevelCappedForGrant } from '@/services/spellPoolDisplay';
import type { ActiveSpellGrant } from '@/services/spellGrants';
import { SpellCard, spellTagsForCard } from '@/components/molecules';

/**
 * **FI-36 + FI-67 (PR-A2 Step 5b)** — Pannello SSOT per la visualizzazione
 * di una grant spell del PG, sia in builder L1 che in level-up.
 *
 * Sostituisce 5 sub-componenti ad-hoc (`MagoNativeSpellList`,
 * `ChiericoNativeSpellList`, `DruidoNativeSpellList`, `PaladinoNativeSpellList`,
 * `WarlockNativeSpellList`) + il pannello pick di `InteractiveGrantSection`
 * nel level-up, allineando l'UI di:
 *  - trucchetti auto / spell L0 auto,
 *  - selezione interattiva di L1 (builder),
 *  - selezione interattiva multi-livello (level-up Magnus/Tomo/Rivelazioni).
 *
 * **Grouping per livello (FI-67)**: se il pool ha spell di livelli diversi,
 * vengono raggruppati con un sotto-header `Livello N` per ciascun livello
 * presente. Per pool single-level il header esterno è già sufficiente — niente
 * grouping interno (ridondante).
 *
 * Il pool è **pre-filtrato dal caller** (via {@link getSpellPoolForGrantInContext}):
 * il pannello è context-agnostic, non sa nulla di Tradizione/archetipo. Si
 * limita a renderizzare quanto ricevuto.
 */

export interface SpellPickPanelProps {
  /** Grant attiva del PG (per leggere `count`, `levelRule`, `perLevelCap`). */
  grant: ActiveSpellGrant;
  /** Pool pre-filtrato dal caller (Tradizione + archetipo + alreadyKnown già applicati). */
  pool: readonly Spell[];
  /**
   * Spell IDs attualmente selezionati per QUESTA grant. Per grant "auto"
   * (`all-of-level` / `fixed-list`) viene ignorato (il pannello è read-only).
   */
  selectedIds: readonly string[];
  /** Callback al toggle di una selezione. Solo per grant "pick". */
  onChange: (next: string[]) => void;
  /** Header titolo (es. «Trucchetti del Mago — concessi automaticamente»). */
  headerText: string;
  /** Subtitle italica opzionale sotto l'header (es. note sull'archetipo). */
  introNote?: ReactNode;
  /**
   * Per il flow level-up: livello da raggiungere (per badge «Nuovo a L{N}»
   * su spell nuovi). Se omesso, niente badge.
   */
  targetLevel?: number;
  /** Livello PG pre-level-up (per badge «Nuovo»). */
  characterLevelBefore?: number;
  /**
   * Prefisso per gli `htmlFor` dei checkbox (per evitare collisioni quando
   * più pannelli convivono nella stessa pagina, es. builder L1).
   */
  inputIdPrefix?: string;
}

export function SpellPickPanel(props: SpellPickPanelProps) {
  const {
    grant,
    pool,
    selectedIds,
    onChange,
    headerText,
    introNote,
    targetLevel,
    characterLevelBefore,
    inputIdPrefix,
  } = props;

  const isAuto = isAutoSpellGrantLevelRule(grant.grant.levelRule);
  const count = grant.grant.count ?? 0;

  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const toggleExpand = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const togglePick = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((x) => x !== id));
      return;
    }
    if (selectedIds.length >= count) return;
    onChange([...selectedIds, id]);
  };

  const grouped = useMemo(() => groupSpellsByLevel(pool), [pool]);
  const showGrouping = grouped.length > 1;
  const idPrefix = inputIdPrefix ?? `spell-${grant.moveId}-${grant.grantIndex}`;

  return (
    <section className="flex flex-col gap-2">
      <PanelHeader
        text={headerText}
        isAuto={isAuto}
        count={count}
        selectedCount={selectedIds.length}
      />
      {introNote ? <p className="text-ink-soft text-sm italic">{introNote}</p> : null}
      <div className="flex flex-col gap-4">
        {grouped.map(({ level, spells }) => (
          <div key={level} className="flex flex-col gap-2">
            {showGrouping ? <LevelSubHeader level={level} /> : null}
            <div className="grid grid-cols-1 gap-[0.7rem] md:grid-cols-2">
              {spells.map((spell) => (
                <SpellRow
                  key={spell.id}
                  spell={spell}
                  isAuto={isAuto}
                  selectedIds={selectedIds}
                  count={count}
                  perLevelCap={grant.grant.perLevelCap}
                  characterLevelBefore={characterLevelBefore}
                  targetLevel={targetLevel}
                  inputId={`${idPrefix}-${spell.id}`}
                  expanded={expanded.has(spell.id)}
                  onExpandToggle={() => toggleExpand(spell.id)}
                  onPickToggle={() => togglePick(spell.id)}
                />
              ))}
            </div>
          </div>
        ))}
        {pool.length === 0 ? (
          <p className="text-ink-soft text-sm italic">Nessun incantesimo disponibile.</p>
        ) : null}
      </div>
    </section>
  );
}

function PanelHeader({
  text,
  isAuto,
  count,
  selectedCount,
}: {
  text: string;
  isAuto: boolean;
  count: number;
  selectedCount: number;
}) {
  if (isAuto) {
    return (
      <InkBlock variant="full">
        {text}
        <span className="text-paper-bright/70 ml-2 text-xs italic normal-case tracking-normal">
          (concessi automaticamente)
        </span>
      </InkBlock>
    );
  }
  const isValid = selectedCount === count;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <InkBlock variant="full" className="flex-1">
        {text}
      </InkBlock>
      <em className="font-display text-ink-soft text-[0.8rem] tracking-[0.06em] whitespace-nowrap">
        {selectedCount} di {count} {isValid ? '✓' : ''}
      </em>
    </div>
  );
}

function LevelSubHeader({ level }: { level: number }) {
  const label = level === 0 ? 'Livello 0 (trucchetti)' : `Livello ${level}`;
  return (
    <span className="font-display text-ink-soft text-[0.75rem] tracking-[0.1em] uppercase">
      {label}
    </span>
  );
}

interface SpellRowProps {
  spell: Spell;
  isAuto: boolean;
  selectedIds: readonly string[];
  count: number;
  perLevelCap: Readonly<Record<string, number>> | undefined;
  characterLevelBefore: number | undefined;
  targetLevel: number | undefined;
  inputId: string;
  expanded: boolean;
  onExpandToggle: () => void;
  onPickToggle: () => void;
}

function SpellRow(props: SpellRowProps) {
  if (props.isAuto) return <AutoSpellRow {...props} />;
  return <PickSpellRow {...props} />;
}

function AutoSpellRow({
  spell,
  characterLevelBefore,
  targetLevel,
  expanded,
  onExpandToggle,
}: SpellRowProps) {
  const isNew =
    characterLevelBefore !== undefined &&
    targetLevel !== undefined &&
    spell.level > characterLevelBefore &&
    spell.level <= targetLevel;
  return (
    <SpellCard
      spell={spell}
      expanded={expanded}
      onToggle={onExpandToggle}
      tags={spellTagsForCard(spell, 'auto')}
      leading={isNew && targetLevel !== undefined ? <NewBadge level={targetLevel} /> : null}
    />
  );
}

function PickSpellRow({
  spell,
  selectedIds,
  count,
  perLevelCap,
  inputId,
  expanded,
  onExpandToggle,
  onPickToggle,
}: SpellRowProps) {
  const checked = selectedIds.includes(spell.id);
  const cappedByCount = !checked && selectedIds.length >= count;
  const cappedByLevel = !checked && isLevelCappedForGrant(spell.level, selectedIds, perLevelCap);
  const disabled = cappedByCount || cappedByLevel;

  return (
    <SpellCard
      spell={spell}
      expanded={expanded}
      onToggle={onExpandToggle}
      tags={spellTagsForCard(spell, checked ? 'picked' : 'pick')}
      labelHtmlFor={inputId}
      selected={checked}
      disabled={disabled}
      leading={
        <Checkbox
          id={inputId}
          checked={checked}
          disabled={disabled}
          onCheckedChange={onPickToggle}
          className="mt-1"
        />
      }
    />
  );
}

function NewBadge({ level }: { level: number }) {
  return (
    <span className="bg-accent-dwr text-paper-bright font-display mt-1 inline-block px-[0.45em] py-[0.1em] text-[0.6rem] tracking-[0.1em] whitespace-nowrap uppercase">
      Nuovo a L{level}
    </span>
  );
}

interface GroupedLevel {
  level: number;
  spells: readonly Spell[];
}

function groupSpellsByLevel(pool: readonly Spell[]): readonly GroupedLevel[] {
  const byLevel = new Map<number, Spell[]>();
  for (const spell of pool) {
    const arr = byLevel.get(spell.level);
    if (arr) arr.push(spell);
    else byLevel.set(spell.level, [spell]);
  }
  return [...byLevel.entries()]
    .sort(([a], [b]) => a - b)
    .map(([level, spells]) => ({ level, spells }));
}
